import React, { useState, useEffect } from 'react'
import {
    Modal,
    Slide,
    Paper,
    Typography,
    IconButton,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Divider,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import {
    Close,
    Add,
    Delete,
    ArrowUpward,
    ArrowDownward,
    Edit,
    Check,
    Lock,
} from '@material-ui/icons'
import { Container } from '../../components'
import {
    useQuestionConfig,
    QuestionConfig,
    QuestionConfigPayload,
} from '../../hooks/useQuestionConfig'

interface Props {
    open: boolean
    onClose: () => void
    apartmentId: number
    apartmentName: string
}

const QUESTION_TYPE_LABELS: Record<string, string> = {
    YES_NO: 'Yes / No',
    TEXT: 'Free Text',
    RATING: 'Rating (1-10)',
}

export const QuestionConfigModal: React.FC<Props> = ({
    open,
    onClose,
    apartmentId,
    apartmentName,
}) => {
    const theme = useTheme()
    const {
        questions,
        setQuestions,
        loading,
        getQuestions,
        createQuestion,
        updateQuestion,
        deleteQuestion,
    } = useQuestionConfig()

    const [showAddForm, setShowAddForm] = useState(false)
    const [newText, setNewText] = useState('')
    const [newType, setNewType] = useState<'RATING' | 'TEXT' | 'YES_NO'>(
        'YES_NO',
    )
    const [newRequired, setNewRequired] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editText, setEditText] = useState('')

    useEffect(() => {
        if (open && apartmentId) {
            getQuestions(apartmentId)
            setShowAddForm(false)
            setEditingId(null)
        }
    }, [open, apartmentId])

    const handleAdd = async () => {
        if (!newText.trim()) return
        const maxOrder = questions.reduce((max, q) => Math.max(max, q.order), 0)
        const payload: QuestionConfigPayload = {
            apartment_id: apartmentId,
            question_text: newText.trim(),
            question_type: newType,
            required: newRequired,
            order: maxOrder + 1,
        }
        const result = await createQuestion(payload)
        if (result) {
            setNewText('')
            setNewType('YES_NO')
            setNewRequired(false)
            setShowAddForm(false)
        }
    }

    const handleReorder = (
        question: QuestionConfig,
        direction: 'up' | 'down',
    ) => {
        const sorted = [...questions].sort((a, b) => a.order - b.order)
        const idx = sorted.findIndex((q) => q.id === question.id)
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1
        if (swapIdx < 0 || swapIdx >= sorted.length) return

        const other = sorted[swapIdx]
        const qOrder = question.order
        const oOrder = other.order
        // Optimistic UI update
        setQuestions((prev) =>
            prev.map((q) => {
                if (q.id === question.id) return { ...q, order: oOrder }
                if (q.id === other.id) return { ...q, order: qOrder }
                return q
            }),
        )
        updateQuestion(question.id, apartmentId, { order: oOrder })
        updateQuestion(other.id, apartmentId, { order: qOrder })
    }

    const handleStartEdit = (q: QuestionConfig) => {
        setEditingId(q.id)
        setEditText(q.question_text)
    }

    const handleSaveEdit = async (id: number) => {
        if (!editText.trim()) return
        await updateQuestion(id, apartmentId, {
            question_text: editText.trim(),
        })
        setEditingId(null)
    }

    const sortedQuestions = [...questions].sort((a, b) => a.order - b.order)

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        padding: 0,
                        width: '90vw',
                        maxWidth: 600,
                        maxHeight: '80vh',
                        borderRadius: 12,
                        outline: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: theme.spacing(2, 3),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            flexShrink: 0,
                        }}
                    >
                        <div>
                            <Typography
                                variant="h6"
                                style={{ fontWeight: 600 }}
                            >
                                Manage Survey Questions
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {apartmentName}
                            </Typography>
                        </div>
                        <IconButton size="small" onClick={onClose}>
                            <Close />
                        </IconButton>
                    </div>

                    {/* Body */}
                    <div
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: theme.spacing(2, 3),
                        }}
                    >
                        {loading ? (
                            <Container
                                style={{
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: theme.spacing(6, 0),
                                }}
                            >
                                <CircularProgress size={36} />
                            </Container>
                        ) : (
                            <>
                                {/* Default questions */}
                                <Typography
                                    variant="overline"
                                    color="textSecondary"
                                    style={{
                                        fontWeight: 600,
                                        marginBottom: theme.spacing(1),
                                        display: 'block',
                                    }}
                                >
                                    Default Questions
                                </Typography>
                                {[
                                    {
                                        text: 'Overall Rating (1-10)',
                                        type: 'Rating',
                                    },
                                    { text: 'Comments', type: 'Free Text' },
                                ].map((dq) => (
                                    <Paper
                                        key={dq.text}
                                        variant="outlined"
                                        style={{
                                            padding: theme.spacing(1.5, 2),
                                            marginBottom: theme.spacing(1),
                                            borderRadius: 8,
                                            backgroundColor:
                                                theme.palette.grey[50],
                                            borderColor:
                                                theme.palette.grey[200],
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: theme.spacing(1),
                                            }}
                                        >
                                            <Lock
                                                style={{
                                                    fontSize: 16,
                                                    color:
                                                        theme.palette.grey[400],
                                                }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 500 }}
                                                >
                                                    {dq.text}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    {dq.type} &bull; Required
                                                    &bull; Always included
                                                </Typography>
                                            </div>
                                        </div>
                                    </Paper>
                                ))}

                                <Typography
                                    variant="overline"
                                    color="textSecondary"
                                    style={{
                                        fontWeight: 600,
                                        marginTop: theme.spacing(2),
                                        marginBottom: theme.spacing(1),
                                        display: 'block',
                                    }}
                                >
                                    Custom Questions
                                </Typography>

                                {/* Question list */}
                                {sortedQuestions.length === 0 && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{
                                            textAlign: 'center',
                                            padding: theme.spacing(3, 0),
                                        }}
                                    >
                                        No custom questions configured.
                                    </Typography>
                                )}

                                {sortedQuestions.map((q, idx) => (
                                    <Paper
                                        key={q.id}
                                        variant="outlined"
                                        style={{
                                            padding: theme.spacing(1.5, 2),
                                            marginBottom: theme.spacing(1),
                                            borderRadius: 8,
                                            opacity: q.active ? 1 : 0.5,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: theme.spacing(1),
                                            }}
                                        >
                                            {/* Reorder buttons */}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    disabled={idx === 0}
                                                    onClick={() =>
                                                        handleReorder(q, 'up')
                                                    }
                                                    style={{ padding: 2 }}
                                                >
                                                    <ArrowUpward
                                                        style={{ fontSize: 16 }}
                                                    />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    disabled={
                                                        idx ===
                                                        sortedQuestions.length -
                                                            1
                                                    }
                                                    onClick={() =>
                                                        handleReorder(q, 'down')
                                                    }
                                                    style={{ padding: 2 }}
                                                >
                                                    <ArrowDownward
                                                        style={{ fontSize: 16 }}
                                                    />
                                                </IconButton>
                                            </div>

                                            {/* Question text / edit */}
                                            <div style={{ flex: 1 }}>
                                                {editingId === q.id ? (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            gap: theme.spacing(
                                                                1,
                                                            ),
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        <TextField
                                                            value={editText}
                                                            onChange={(e) =>
                                                                setEditText(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            size="small"
                                                            variant="outlined"
                                                            fullWidth
                                                            autoFocus
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key ===
                                                                    'Enter'
                                                                )
                                                                    handleSaveEdit(
                                                                        q.id,
                                                                    )
                                                                if (
                                                                    e.key ===
                                                                    'Escape'
                                                                )
                                                                    setEditingId(
                                                                        null,
                                                                    )
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleSaveEdit(
                                                                    q.id,
                                                                )
                                                            }
                                                        >
                                                            <Check
                                                                fontSize="small"
                                                                color="primary"
                                                            />
                                                        </IconButton>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <Typography
                                                            variant="body2"
                                                            style={{
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {q.question_text}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            {QUESTION_TYPE_LABELS[
                                                                q.question_type
                                                            ] ||
                                                                q.question_type}
                                                            {q.required &&
                                                                ' • Required'}
                                                        </Typography>
                                                    </>
                                                )}
                                            </div>

                                            {/* Actions */}
                                            {editingId !== q.id && (
                                                <>
                                                    <Tooltip
                                                        title={
                                                            q.active
                                                                ? 'Disable'
                                                                : 'Enable'
                                                        }
                                                    >
                                                        <Switch
                                                            checked={q.active}
                                                            onChange={() =>
                                                                updateQuestion(
                                                                    q.id,
                                                                    apartmentId,
                                                                    {
                                                                        active: !q.active,
                                                                    },
                                                                )
                                                            }
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleStartEdit(
                                                                    q,
                                                                )
                                                            }
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                deleteQuestion(
                                                                    q.id,
                                                                    apartmentId,
                                                                )
                                                            }
                                                        >
                                                            <Delete
                                                                fontSize="small"
                                                                color="error"
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </div>
                                    </Paper>
                                ))}

                                <Divider
                                    style={{ margin: theme.spacing(2, 0) }}
                                />

                                {/* Add question */}
                                {showAddForm ? (
                                    <Paper
                                        variant="outlined"
                                        style={{
                                            padding: theme.spacing(2),
                                            borderRadius: 8,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            style={{
                                                fontWeight: 600,
                                                marginBottom: theme.spacing(
                                                    1.5,
                                                ),
                                            }}
                                        >
                                            New Question
                                        </Typography>
                                        <TextField
                                            label="Question Text"
                                            value={newText}
                                            onChange={(e) =>
                                                setNewText(e.target.value)
                                            }
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            autoFocus
                                            inputProps={{ maxLength: 500 }}
                                            style={{
                                                marginBottom: theme.spacing(
                                                    1.5,
                                                ),
                                            }}
                                        />
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: theme.spacing(2),
                                                alignItems: 'center',
                                                marginBottom: theme.spacing(
                                                    1.5,
                                                ),
                                            }}
                                        >
                                            <FormControl
                                                variant="outlined"
                                                size="small"
                                                style={{ minWidth: 160 }}
                                            >
                                                <InputLabel>Type</InputLabel>
                                                <Select
                                                    value={newType}
                                                    onChange={(e) =>
                                                        setNewType(
                                                            e.target.value as
                                                                | 'RATING'
                                                                | 'TEXT'
                                                                | 'YES_NO',
                                                        )
                                                    }
                                                    label="Type"
                                                >
                                                    <MenuItem value="YES_NO">
                                                        Yes / No
                                                    </MenuItem>
                                                    <MenuItem value="TEXT">
                                                        Free Text
                                                    </MenuItem>
                                                    <MenuItem value="RATING">
                                                        Rating (1-10)
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={newRequired}
                                                        onChange={(e) =>
                                                            setNewRequired(
                                                                e.target
                                                                    .checked,
                                                            )
                                                        }
                                                        size="small"
                                                        color="primary"
                                                    />
                                                }
                                                label="Required"
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: theme.spacing(1),
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    setShowAddForm(false)
                                                    setNewText('')
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={handleAdd}
                                                disabled={!newText.trim()}
                                                style={{
                                                    textTransform: 'none',
                                                }}
                                            >
                                                Add Question
                                            </Button>
                                        </div>
                                    </Paper>
                                ) : (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        startIcon={<Add />}
                                        onClick={() => setShowAddForm(true)}
                                        style={{ textTransform: 'none' }}
                                    >
                                        Add Question
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </Paper>
            </Slide>
        </Modal>
    )
}
